import {
  Button,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { NavigateForwardIcon } from 'components/icons';
import { Owner } from 'components/ownership/Owner';
import { OwnerPanel, OwnerPanelTitle } from 'components/ownership/OwnerPanel';
import SpaceIcon from 'components/SpaceIcon';
import { useSpaceColor } from 'hooks/useSpaceColor';
import { OverlayProps } from 'components/TaskOverlay';
import React from 'react';
import { useFragment } from 'utils/graphClient';
import { generatePath } from 'react-router-dom';
import { graphql } from 'relay-runtime';
import { notEmpty } from 'utils/NotEmptyFilter';
import { SPACE_PATH } from 'utils/routes';
import { SpaceInfoModal_space$key } from './__generated__/SpaceInfoModal_space.graphql';

const spaceFragment = graphql`
  fragment SpaceInfoModal_space on Space {
    id
    name
    color {
      light
      dark
    }
    ownerships {
      edges {
        node {
          ...Owner_ownership
          explicitOwner
          member {
            id
            status {
              value
            }
          }
        }
      }
    }
  }
`;

interface SpaceInfoModalProps extends OverlayProps {
  space: SpaceInfoModal_space$key;
  disableVisitButton?: boolean;
}

export const SpaceInfoModal = ({
  space: _space,
  disableVisitButton,
  ...overlayProps
}: SpaceInfoModalProps) => {
  const space = useFragment(spaceFragment, _space);
  const { name, id, ownerships } = space || {};
  const spaceColor = useSpaceColor(space);
  const explicitOwnerships =
    ownerships?.edges
      ?.filter(
        (ownership) =>
          !!ownership?.node?.explicitOwner &&
          ownership.node.member.status?.value !== 'DELETED',
      )
      .map((ownership) => ownership?.node)
      .filter(notEmpty) || [];
  return (
    <Modal {...overlayProps}>
      <ModalOverlay />
      <ModalContent data-qa="space-info-dialog">
        <ModalHeader>
          <HStack>
            <SpaceIcon color={spaceColor} />
            <Text>
              {name} - {t`info`}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack>
            <OwnerPanel title={<OwnerPanelTitle>{t`Owners`}</OwnerPanelTitle>}>
              {explicitOwnerships.length === 0 ? (
                <Text>{t`No owners`}</Text>
              ) : (
                explicitOwnerships.map((ownership) => (
                  <Owner key={ownership.member.id} ownership={ownership} />
                ))
              )}
            </OwnerPanel>
            {!disableVisitButton && (
              <HStack>
                <Spacer />
                <Button
                  variant="outline"
                  colorScheme="blue"
                  size="sm"
                  as={Link}
                  to={generatePath(SPACE_PATH, { spaceId: id })}
                >
                  {t`Visit space`}
                  <NavigateForwardIcon />
                </Button>
              </HStack>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
